import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiClient';
import {
  HomeIcon,
  QueueListIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  HandRaisedIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import LlmHealthBanner from './LlmHealthBanner';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Review Queue', href: '/queue', icon: QueueListIcon },
  { name: 'Responses', href: '/responses', icon: ChatBubbleLeftRightIcon },
  { name: 'Escalated to person', href: '/escalations', icon: HandRaisedIcon },
  // Open to agents, not admins only: the people working the queue are the
  // ones who spot a pattern first, and a page they can't open is a page
  // nobody reads.
  { name: 'Quote insights', href: '/quote-insights', icon: CurrencyDollarIcon },
  // Spend and conversion on one page. Open to everyone for the same reason as
  // quote insights, and because "the replies went slow on Tuesday" is noticed
  // by whoever was working the queue on Tuesday.
  { name: 'AI usage', href: '/ai-usage', icon: ChartBarIcon },
  { name: 'Ticket History', href: '/tickets', icon: EnvelopeIcon },
  { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  // Closed by default (mobile-first). On large screens the sidebar is always
  // shown via `lg:translate-x-0`, so this state only drives the mobile drawer.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  // Outstanding-escalations badge on the "Escalated to person" nav item.
  const [escalatedCount, setEscalatedCount] = useState(0);
  const fetchEscalatedCount = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/v1/queue/escalated/count');
      setEscalatedCount(res.data?.count || 0);
    } catch (_) { /* silent — the badge is a nicety, never block the nav */ }
  }, []);

  useEffect(() => {
    fetchEscalatedCount();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchEscalatedCount();
    }, 45000);
    // Refetch immediately when the Escalations page changes something, or on focus.
    const onChanged = () => fetchEscalatedCount();
    window.addEventListener('escalations-changed', onChanged);
    window.addEventListener('focus', onChanged);
    return () => {
      clearInterval(id);
      window.removeEventListener('escalations-changed', onChanged);
      window.removeEventListener('focus', onChanged);
    };
  }, [fetchEscalatedCount]);

  const filteredNavigation = navigation.filter(
    (item) => !item.adminOnly || user?.role === 'admin'
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile backdrop — tap to close the drawer (desktop never shows this) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — off-canvas drawer on mobile, always visible on lg+ */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 bg-white border-b border-gray-200">
            <img src="/michanic-logo.svg" alt="Michanic" className="h-10" style={{ transform: 'scale(1.1)' }} />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeSidebar}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-red-50 text-michanic'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  <span className="flex-1">{item.name}</span>
                  {item.href === '/escalations' && escalatedCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-white bg-red-600 rounded-full">
                      {escalatedCount > 99 ? '99+' : escalatedCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-michanic rounded-full flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Link
              to="/logs"
              onClick={closeSidebar}
              className={`flex items-center w-full px-4 py-2 mb-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === '/logs'
                  ? 'bg-red-50 text-michanic'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DocumentTextIcon className="w-5 h-5 mr-3" />
              System Logs
            </Link>
            <button
              onClick={logout}
              className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content — leaves room for the fixed sidebar on lg+ only */}
      <div className="lg:ml-64">
        {/* Degraded-AI banner: sits ABOVE the top bar so it is the first thing
            seen on every page while the LLM API is failing (it clears itself). */}
        <LlmHealthBanner />

        {/* Top bar */}
        <div className="bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-600 rounded-lg hover:bg-gray-100 lg:hidden"
              aria-label="Toggle navigation"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="text-sm text-gray-600">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
